import { useNavigate } from "react-router-dom";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Star, Clock, Tag, ShieldCheck, Sparkles, ArrowRight, RotateCcw, Pencil, Headphones, ChevronDown } from "lucide-react";
import Header from "@/components/Header";
import SEO from "@/components/SEO";
import { useReveal } from "@/hooks/useReveal";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAssessmentCtaState } from "@/hooks/useAssessmentCtaState";
import { useUnlocks } from "@/hooks/useUnlocks";
import RetakeAssessmentDialog from "@/components/RetakeAssessmentDialog";
import BrandHero from "@/components/home/BrandHero";
import DeferUntilVisible from "@/components/DeferUntilVisible";
import avatar1 from "@/assets/avatar-1.jpg";
import avatar2 from "@/assets/avatar-2.jpg";
import avatar3 from "@/assets/avatar-3.jpg";
import avatar4 from "@/assets/avatar-4.jpg";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// Below-the-fold sections are lazy-loaded so their JS (including the heavy
// Lottie player) ships in separate chunks and doesn't bloat the initial
// homepage bundle.
const BestFitCareersSection = lazy(() => import("@/components/home/BestFitCareersSection"));
const HowItWorksSteps = lazy(() => import("@/components/home/HowItWorksSteps"));
const TestimonialsSection = lazy(() => import("@/components/home/TestimonialsSection"));
const FigureOutSection = lazy(() => import("@/components/home/FigureOutSection"));
const RoadmapSection = lazy(() => import("@/components/home/RoadmapSection"));
const Footer = lazy(() => import("@/components/Footer"));
/* Final CTA with clouds that disperse outward as you scroll through.
   Scroll progress is written to a CSS variable (--p) on the section element,
   so React does not re-render on scroll. Transforms read --p in inline styles. */
const FinalCta = ({ onEnter, label = "Start the assessment" }: { onEnter: () => void; label?: string }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  const cloudRefs = useRef<(HTMLDivElement | null)[]>([]);
  // Treat anything below the lg breakpoint (1024px) as "compact" - clouds
  // disperse outward from centre instead of converging inward. This covers
  // both mobile and tablet, which previously fell into the desktop branch
  // and pushed clouds outside the narrower card.
  const [isCompact, setIsCompact] = useState<boolean>(() =>
    typeof window !== "undefined" ? window.innerWidth < 1024 : false,
  );
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 1023px)");
    const onChange = () => setIsCompact(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  const cloudOffsets = [
    [-260, 180],
    [280, -20],
    [220, -160],
  ] as const;

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const sticky = stickyRef.current;
    if (!wrapper || !sticky) return;
    let raf = 0;
    let last = -1;
    const update = () => {
      raf = 0;
      const rect = wrapper.getBoundingClientRect();
      const vh = window.innerHeight;
      const stickyH = sticky.offsetHeight;
      const travel = Math.max(1, wrapper.offsetHeight - stickyH);
      const centerOffset = (vh - stickyH) / 2;
      sticky.style.top = `${centerOffset}px`;
      const p = Math.max(0, Math.min(1, (centerOffset - rect.top) / travel));
      const q = Math.round(p * 1000) / 1000;
      if (q !== last) {
        last = q;
        sticky.style.setProperty("--p", String(q));
        if (isCompact) {
          // Compact (mobile + tablet): clouds start centred over the heading
          // (-50%, -50%) at p=0 and disperse outward as p grows. Use a stronger
          // finish on tablet so the clouds clear the heading fully.
          const w = window.innerWidth;
          const scale = w < 768 ? 0.85 : Math.min(1.25, w / 768);
          cloudOffsets.forEach(([dx, dy], index) => {
            const cloud = cloudRefs.current[index];
            if (!cloud) return;
            cloud.style.transform = `translate3d(calc(-50% + ${q * dx * scale}px), calc(-50% + ${q * dy * scale}px), 0)`;
          });
        }
      }
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [isCompact]);

  // Desktop (>=1024px): clouds start dispersed (p=0) and converge to centre as you scroll (p=1).
  // Compact (<1024px): clouds start centred over the heading and disperse outward, applied
  // imperatively in the scroll handler above (which also handles the -50%/-50% centring).
  const cloudStyle = (dx: number, dy: number) => {
    if (isCompact) {
      return { transform: "translate3d(-50%, -50%, 0)" } as React.CSSProperties;
    }
    return {
      transform: `translate3d(calc((1 - var(--p, 0)) * ${dx}px), calc((1 - var(--p, 0)) * ${dy}px), 0)`,
    } as React.CSSProperties;
  };

  // Position classes - compact pins to centre, lg+ uses the original dispersed anchors.
  const cloudPos = [
    "top-1/2 left-1/2 lg:top-[12%] lg:left-auto lg:right-[6%]",
    "top-1/2 left-1/2 lg:top-[42%] lg:left-[4%]",
    "top-1/2 left-1/2 lg:top-auto lg:left-[18%] lg:bottom-[14%]",
  ];

  return (
    // Outer wrapper provides scroll length for the pin (~80vh of pinned scroll).
    // Mobile uses a shorter wrapper so the section is more compact.
    <section
      ref={wrapperRef}
      className="w-full px-4 md:px-8 pb-12 h-[110vh] md:h-[130vh]"
    >

      {/* Sticky pinned stage - locks only once the card itself reaches viewport center */}
      <div
        ref={stickyRef}
        className="sticky w-full max-w-7xl mx-auto"
        style={{ ["--p" as never]: 0 }}
      >
        <div className="relative w-full bg-tk-primary rounded-[2.5rem] md:rounded-[4rem] overflow-hidden py-20 md:py-44 px-6 md:px-8 flex flex-col items-center justify-center text-center isolate">
          <div
            aria-hidden
            className="absolute -bottom-64 -right-32 w-[40rem] h-[50rem] bg-white/10 rotate-12 rounded-t-[20rem] -z-10"
          />
          {/* Soft clouds - dispersed positions; translate toward center when progress = 0 */}
          <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
            <div
              ref={(el) => {
                cloudRefs.current[0] = el;
              }}
              className={`cloud-drift-a absolute ${cloudPos[0]} w-[28rem] h-32 opacity-90`}
              style={cloudStyle(-260, 180)}
            >
              <div className="absolute left-0 top-6 w-40 h-24 bg-white/85 rounded-full blur-xl" />
              <div className="absolute left-20 top-0 w-44 h-28 bg-white/90 rounded-full blur-xl" />
              <div className="absolute left-44 top-4 w-40 h-24 bg-white/80 rounded-full blur-xl" />
              <div className="absolute left-64 top-8 w-32 h-20 bg-white/70 rounded-full blur-xl" />
            </div>
            <div
              ref={(el) => {
                cloudRefs.current[1] = el;
              }}
              className={`cloud-drift-b absolute ${cloudPos[1]} w-[26rem] h-32 opacity-90`}
              style={cloudStyle(280, -20)}
            >
              <div className="absolute left-0 top-6 w-36 h-24 bg-white/80 rounded-full blur-xl" />
              <div className="absolute left-16 top-0 w-44 h-28 bg-white/90 rounded-full blur-xl" />
              <div className="absolute left-40 top-4 w-40 h-24 bg-white/85 rounded-full blur-xl" />
              <div className="absolute left-60 top-10 w-28 h-16 bg-white/65 rounded-full blur-xl" />
            </div>
            <div
              ref={(el) => {
                cloudRefs.current[2] = el;
              }}
              className={`cloud-drift-c absolute ${cloudPos[2]} w-56 h-20 opacity-70`}
              style={cloudStyle(220, -160)}
            >
              <div className="absolute left-0 top-3 w-24 h-14 bg-white/70 rounded-full blur-xl" />
              <div className="absolute left-10 top-0 w-28 h-16 bg-white/80 rounded-full blur-xl" />
              <div className="absolute left-24 top-4 w-24 h-14 bg-white/65 rounded-full blur-xl" />
            </div>
          </div>
          <h2 className="font-editorial text-5xl sm:text-6xl md:text-8xl text-tk-bg leading-[0.9] tracking-tighter mb-12 text-balance">
            Your future, <br /> unclouded.
          </h2>
          <button
            onClick={onEnter}
            className="bg-tk-bg text-tk-text px-10 md:px-12 py-5 md:py-6 rounded-full text-base md:text-lg font-medium hover:scale-[1.04] transition-transform shadow-2xl"
          >
            {label}
          </button>
          <p className="mt-8 text-tk-bg/90 text-xs md:text-sm font-medium tracking-[0.22em] uppercase">
            Free · 3 minutes · No sign-up
          </p>
        </div>
      </div>
    </section>
  );
};

/* Sequential reveal for the 3-step section.
   Numbers (01/02/03) are visible by default.
   Phase 1: titles fade in one by one (Answer -> Match -> Unlock).
   Phase 2: descriptions fade in one by one. */
const StepsRow = ({
  steps,
}: {
  steps: { n: string; title: string; copy: string }[];
}) => {
  const { ref, shown } = useReveal<HTMLDivElement>({ threshold: 0.2, rootMargin: "0px 0px -10% 0px" });
  const isMobile = useIsMobile();
  const TITLE_STAGGER = 500; // ms between each title
  const TITLE_DURATION = 700;
  const COPY_STAGGER = 350;
  const titlesEndTime = (steps.length - 1) * TITLE_STAGGER + TITLE_DURATION;

  const fadeStyle = (delay: number, duration = 700): React.CSSProperties => {
    if (isMobile) {
      // No animation on mobile - content always visible.
      return { opacity: 1, transform: "translateY(0)" };
    }
    return shown
      ? {
          opacity: 1,
          transform: "translateY(0)",
          transition: `opacity ${duration}ms ease-out ${delay}ms, transform ${duration}ms cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
        }
      : { opacity: 0, transform: "translateY(10px)" };
  };

  return (
    <div ref={ref} className="grid md:grid-cols-3 gap-12 md:gap-10">
      {steps.map((s, i) => {
        const titleDelay = i * TITLE_STAGGER;
        const copyDelay = titlesEndTime + i * COPY_STAGGER;

        return (
          <div key={s.n} className="text-left">
            <span className="block font-editorial text-tk-primary text-xl tracking-wide mb-2">
              {s.n}
            </span>
            <h3
              className="font-editorial text-3xl md:text-4xl text-tk-text mb-4"
              style={fadeStyle(titleDelay, TITLE_DURATION)}
            >
              {s.title}.
            </h3>
            <p
              className="text-base text-tk-text/60 leading-relaxed max-w-[34ch]"
              style={fadeStyle(copyDelay)}
            >
              {s.copy}
            </p>
          </div>
        );
      })}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*  Reveal wrapper                                                            */
/* -------------------------------------------------------------------------- */
const Reveal = ({
  children,
  delay = 0,
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => {
  const { ref, shown } = useReveal();
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-[800ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
        shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      } ${className}`}
    >
      {children}
    </div>
  );
};

/* Count up from 0 to `to` once the element scrolls into view. */
const CountUp = ({ to, duration = 2800 }: { to: number; duration?: number }) => {
  const { ref, shown } = useReveal<HTMLSpanElement>({
    threshold: 0.4,
    rootMargin: "0px 0px -10% 0px",
  });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!shown) return;
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(eased * to));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [shown, to, duration]);

  return <span ref={ref}>{value}</span>;
};

/* -------------------------------------------------------------------------- */
/*  Monumental Exhibition - Homepage                                          */
/* -------------------------------------------------------------------------- */
const Index = () => {
  const navigate = useNavigate();
  const { state: ctaState } = useAssessmentCtaState();
  const { matches, blueprint, accelerator } = useUnlocks();
  const [retakeOpen, setRetakeOpen] = useState(false);

  // State-aware primary CTA logic.
  // Highest tier wins: Career Blueprint > Career Matches.
  const ctaPrimary = (() => {
    if (ctaState === "completed_unlocked") {
      if (blueprint || accelerator) {
        return { label: "Career Blueprint", short: "Career Blueprint", action: () => navigate("/blueprint") };
      }
      // matches (or fallback)
      return { label: "Career Matches", short: "Career Matches", action: () => navigate("/career-matches") };
    }
    if (ctaState === "completed_locked") {
      return { label: "See my results", short: "See My Results", action: () => navigate("/results") };
    }
    // loading or no_assessment -> default brand CTA
    return { label: "Start the assessment", short: "Start the assessment", action: () => navigate("/assessment") };
  })();

  const showCompletedActions = ctaState === "completed_locked" || ctaState === "completed_unlocked";

  const faqs = [
    {
      q: "How is this different from a personality test?",
      a: "Personality tests describe who you are. TonyKnows translates how you think and what you value into specific career matches, with a step-by-step roadmap and salary progression for each one.",
    },
    {
      q: "Is the assessment actually personalised?",
      a: "Yes. Your answers shape your top matches, the order they appear, and the language used in your plan. No two reports are identical.",
    },
    {
      q: "How accurate are the results?",
      a: "Very. Instead of guessing your personality, we map how you actually think, decide and work, then match that pattern against real career profiles. Most people say their top results feel uncomfortably accurate - in a good way.",
    },
    {
      q: "What do I actually get at the end?",
      a: "A clear breakdown of how you operate, your top career matches ranked by fit, and a step-by-step plan for each one - including skills to build, first roles to target and realistic salary progression. No fluff, just a career path you can act on.",
    },
    {
      q: "How long does it take to get my career plan?",
      a: "The free assessment takes about 3 minutes. Your matches appear instantly. The full unlocked plan, including roadmap and timeline, is generated in under a minute.",
    },
  ];

  return (
    <div className="min-h-screen bg-tk-bg text-tk-text font-display flex flex-col antialiased">
      <SEO
        title="TonyKnows - Find the Right Career for You | Free Career Quiz"
        description="Free 3-minute career quiz for students and school leavers. Discover careers that match your strengths, personality and goals - with clear next steps."
        path="/"
      />

      <Header />

      <main>
      {/* ========================= BRAND HERO ========================= */}
      <BrandHero
        primaryLabel={
          ctaState === "no_assessment" || ctaState === "loading"
            ? "Free Career Quiz"
            : ctaPrimary.short
        }
        onPrimary={ctaPrimary.action}
        onSecondary={() => navigate("/careers")}
      />

      {/* ============== BEST-FIT CAREERS - Personalised results ============== */}
      <DeferUntilVisible minHeight="800px">
        <Suspense fallback={<div style={{ minHeight: "800px" }} aria-hidden="true" />}>
          <BestFitCareersSection />
        </Suspense>
      </DeferUntilVisible>

      {/* ===================== HOW IT WORKS STEPS ===================== */}
      <DeferUntilVisible minHeight="800px">
        <Suspense fallback={<div style={{ minHeight: "800px" }} aria-hidden="true" />}>
          <HowItWorksSteps />
        </Suspense>
      </DeferUntilVisible>

      {/* ============== TESTIMONIALS ============== */}
      <DeferUntilVisible minHeight="600px">
        <Suspense fallback={<div style={{ minHeight: "600px" }} aria-hidden="true" />}>
          <TestimonialsSection />
        </Suspense>
      </DeferUntilVisible>

      {/* ============== FIGURE OUT - 4 starting points ============== */}
      <DeferUntilVisible minHeight="700px">
        <Suspense fallback={<div style={{ minHeight: "700px" }} aria-hidden="true" />}>
          <FigureOutSection />
        </Suspense>
      </DeferUntilVisible>

      {/* ============== ROADMAP - mirrored layout ============== */}
      <DeferUntilVisible minHeight="700px">
        <Suspense fallback={<div style={{ minHeight: "700px" }} aria-hidden="true" />}>
          <RoadmapSection />
        </Suspense>
      </DeferUntilVisible>


      {/* ============== STAT MONUMENT and HOW IT WORKS sections removed ============== */}


      {/* ============== FAQ ============== */}
      <section className="w-full pt-10 md:pt-32 pb-8 md:pb-24 px-6 md:px-24 lg:px-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.15fr] gap-12 lg:gap-20 items-start">
          {/* Left: heading + contact */}
          <Reveal>
            <div className="lg:sticky lg:top-28">
              <p className="text-[11px] uppercase tracking-[0.28em] text-tk-primary mb-5 font-semibold">
                Support
              </p>
              <h2 className="font-editorial text-4xl md:text-6xl lg:text-7xl text-tk-text tracking-tighter leading-[1.02] mb-6">
                Get answers,<br />
                move forward<br />
                <span className="text-tk-text">with confidence.</span>
              </h2>
              <div className="w-16 h-px bg-tk-primary/40 mb-8" />
              <p className="text-base md:text-lg text-tk-text/70 leading-relaxed max-w-md mb-10">
                We're here to help you every step of the way. Browse our most common queries or reach out to our team.
              </p>

              <div className="flex items-start gap-5">
                <span className="flex-shrink-0 size-14 rounded-full bg-tk-primary/12 flex items-center justify-center">
                  <Headphones className="size-6 text-tk-primary" strokeWidth={2} />
                </span>
                <div>
                  <p className="font-semibold text-tk-text mb-1">
                    Can't find what you're looking for?
                  </p>
                  <p className="text-tk-text/60 mb-3">
                    Our support team is ready to help.
                  </p>
                  <button
                    onClick={() => navigate("/contact")}
                    className="inline-flex items-center gap-2 text-tk-primary font-semibold hover:gap-3 transition-all"
                  >
                    <span>Contact us</span>
                    <ArrowRight className="size-4" />
                  </button>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Right: accordion */}
          <Reveal delay={120}>
            <div className="rounded-3xl border border-tk-text/10 bg-white/40 p-3 md:p-4">
              <Accordion
                type="single"
                collapsible
                defaultValue="item-0"
                className="flex flex-col"
              >
                {faqs.map((f, i) => (
                  <AccordionItem
                    key={f.q}
                    value={`item-${i}`}
                    className="group border-b border-tk-text/10 last:border-b-0"
                  >
                    <AccordionTrigger
                      headingLevel={3}
                      className="list-none px-4 md:px-6 py-5 md:py-6 flex justify-between items-center gap-6 text-left hover:no-underline [&>svg]:hidden"
                    >
                      <span className="flex items-center gap-4 md:gap-5">
                        <span className="size-9 shrink-0 rounded-full flex items-center justify-center transition-colors border border-tk-text/15 text-tk-text/60 group-data-[state=open]:bg-tk-primary/15 group-data-[state=open]:text-tk-primary group-data-[state=open]:border-transparent">
                          <svg
                            viewBox="0 0 12 12"
                            className="w-3 h-3 transition-transform duration-300 group-data-[state=open]:rotate-45"
                            aria-hidden="true"
                          >
                            <line x1="6" y1="1" x2="6" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            <line x1="1" y1="6" x2="11" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                          </svg>
                        </span>
                        <span className="text-base md:text-lg font-medium not-italic font-display text-tk-text">
                          {f.q}
                        </span>
                      </span>
                      <ChevronDown
                        className="size-5 shrink-0 text-tk-primary transition-transform duration-300 group-data-[state=open]:rotate-180"
                        aria-hidden
                      />
                    </AccordionTrigger>
                    <AccordionContent className="px-4 md:px-6 pb-6 pt-0">
                      <p className="text-tk-text/70 leading-relaxed not-italic font-display whitespace-pre-line pl-[3.25rem] md:pl-[3.5rem]">
                        {f.a}
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </Reveal>
        </div>
      </section>


      {/* ============== FINAL CTA - monumental ============== */}
      {/* ============== FINAL CTA - monumental ============== */}
      <FinalCta onEnter={ctaPrimary.action} label={ctaPrimary.short} />

      </main>

      <Suspense fallback={<div style={{ minHeight: "400px" }} aria-hidden="true" />}>
        <Footer />
      </Suspense>
      <RetakeAssessmentDialog open={retakeOpen} onOpenChange={setRetakeOpen} />
    </div>
  );
};

export default Index;
